import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import bookingsApi, { type MinimalBooking } from '@/src/api/bookingsApi';

/**
 * useFetchBookings — `GET /me/reservations`, behind `app/profile`.
 *
 * The profile page always has a user, so their details render immediately; the
 * reservations list comes off the network and so loads, then succeeds or fails.
 * Fetched once on mount. Unlike [[useFetchEvent]] there's no `id` — the list is
 * "the current user's", identified by the token alone — so the state is a plain
 * discriminated union with no per-id staleness to track.
 *
 * This hook was a dummy until the endpoint was wired in, and the two things it
 * faked (a delay, a `SIMULATE_ERROR` toggle) are gone with it: a real fetch has
 * real latency, and the errored state is now reached by breaking the network
 * rather than by editing a const.
 *
 * ── It owns the wire → row mapping, and that's on purpose ───────────────
 * `listReservations` hands back `Reservation`s, which are booking-shaped: a
 * nested event, a slot, the priced lines. A row draws six things. `toRow` below is
 * where the one becomes the other, so [[bookingsApi]] stays a description of the
 * wire and `ReservationListItem` stays render-only — neither has to know about the
 * other. It's also where `past` comes from, which no field on the wire answers.
 */

/**
 * One reservation as the profile list renders it — the props
 * `ReservationListItem` takes, and nothing else.
 *
 * NOT [[bookingsApi]]'s `Booking`, and not its `Reservation` either: those are the
 * wire, this is the row. The distinction is worth the name, because a row that
 * carried the whole booking would let a card start printing money the list
 * endpoint doesn't even send.
 */
export type ReservationRow = {
  /** The booking id — what `/bookings/{id}` opens when the row is tapped. */
  id: string;
  imageUrl: string;
  title: string;
  /**
   * The slot's START, raw off the wire — UTC with a trailing `Z`, which
   * `formatDateTime` parses to the device's local time. Passed through unformatted
   * per the house rule: components take the raw string and format internally.
   */
  slotDateTime: string;
  location: string;
  /** Derived here — see `toRow`. Nothing on the wire says it. */
  past: boolean;
};

/**
 * The fetch's state as a discriminated union — `bookings` is present iff the
 * fetch succeeded, so a `status === 'success'` check narrows it non-null.
 *
 * `error` is the whole `PSErrorClass` rather than a boolean, for the reason the
 * other fetch hooks give: its `message` is already the right words for the kind of
 * failure (the server's own text on a 4xx, connection copy on a network drop), so
 * the errored section can say something true about what happened.
 *
 * `retry` rides ON the state rather than beside it — the same shape
 * [[useFetchEvent]] returns — so the whole contract travels as one value and a
 * component handed the state can always offer the way out of an error.
 */
export type FetchReservationsState = SettledState & {
  /** Re-run the fetch — straight back to `loading` while it's in the air. */
  retry: () => void;
  /**
   * Re-run the fetch with the list still drawn, and RESOLVE when it settles —
   * pull-to-refresh, which brings its own spinner and has to be told when to stop.
   * The difference from `retry` is only the wait: that one clears to `loading` so
   * the errored section can redraw its bones, this one leaves what's on screen
   * alone, because the reader is still looking at it.
   */
  refresh: () => Promise<void>;
};

/** The fetch's three states, before `retry` is attached. */
type SettledState =
  | { status: 'loading'; reservations: null; error: null }
  | { status: 'error'; reservations: null; error: PSErrorClass }
  | { status: 'success'; reservations: MinimalBooking[]; error: null };

const LOADING: SettledState = {
  status: 'loading',
  reservations: null,
  error: null,
};

/**
 * Fetches the current user's reservations on mount, and again on every `retry` or
 * `refresh`. Starts in `loading`, then settles into `success` (with the rows,
 * ordered) or `error`. The `isMounted` ref drops a resolution that lands after the
 * screen unmounts — a ref rather than an effect-local flag because `refresh` is
 * called from outside the effect and needs the same guard.
 *
 * No session dependency, unlike [[useFetchEvent]]: profile is only reachable
 * signed in, and a sign-out pops the user off the route rather than leaving a
 * stale list on screen.
 *
 * `retry` puts the state back to `loading` and bumps the counter the effect keys
 * off — a counter rather than a bare re-run because a retry that fails the SAME
 * way must still arrive as a fresh `PSErrorClass`, or an effect watching the error
 * would see an unchanged value and never fire again.
 *
 * The returned object is rebuilt every render (no `useMemo`, unlike
 * [[useFetchEvent]]) because nothing here feeds a context — the profile screen
 * reads it directly, so there's no identity for a memo to protect.
 */
export function useFetchReservations(): FetchReservationsState {
  const [state, setState] = useState<SettledState>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const reservations = await bookingsApi.listBookings();
      if (isMounted.current)
        setState({
          status: 'success',
          reservations,
          error: null,
        });
    } catch (error: unknown) {
      // `asApiError` rather than the raw throw: a mapping bug in `toRow` lands
      // in this same catch as a bare TypeError, and the state promises a
      // readable `kind` either way.
      if (isMounted.current)
        setState({
          status: 'error',
          reservations: null,
          error: asApiError(error),
        });
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // False positive: `load` only setStates after awaiting the request, so the
    // write lands in a later microtask, not synchronously in this body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [attempt, load]);

  const retry = useCallback(() => {
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, retry, refresh: load };
}

/**
 * Upcoming first (soonest at the top), then past (most recently finished first).
 *
 * The endpoint orders by `created_at` descending, which is the wrong axis for this
 * screen: it interleaves, so a booking made yesterday for an event that has
 * already happened sits above one made last week for tomorrow night. A reader
 * opening their profile is looking for the next thing they're going to, so that
 * goes at the top and history sinks below it.
 *
 * This is the CLIENT's ordering choice — the server's is untouched and one line
 * (the `.sort` in the hook) reverts to it.
 */
